/**
 * 프레임 유틸리티
 * - 빈 프레임 생성 / 이미지 삽입·교체·추출
 * - clipPath 생성, 히트 테스트
 * - 프레임 편집 모드 (진입·업데이트·종료)
 */
import * as fabric from 'fabric';
import { createShapeObject } from './shapeUtils';
import { applySelectionStyle } from './selectionStyleUtils';

// ─── 상수 ────────────────────────────────────────────────────

/** _orig* 속성 키 목록 (저장/복원/정리에 공통 사용) */
const ORIG_KEYS = [
  '_origLeft', '_origTop', '_origScaleX', '_origScaleY',
  '_origWidth', '_origHeight', '_origAngle', '_origCropX', '_origCropY',
];

/** 프레임 메타 키 목록 */
const FRAME_META_KEYS = [
  '_isFrame', '_isEmptyFrame', '_frameShapeType', '_frameLocked',
  '_frameImageScale', '_frameWidth', '_frameHeight', '_frameImageStack',
  ...ORIG_KEYS,
];

/** 빈 프레임 기본 스타일 */
const EMPTY_FRAME_STYLE = {
  stroke: '#93bbf0',
  strokeWidth: 2,
  strokeDashArray: [8, 4],
  strokeUniform: true,
  paintFirst: 'stroke',
  opacity: 1,
};

/** 빈 프레임 그라데이션 색상 */
const EMPTY_FRAME_GRADIENT = [
  { offset: 0, color: '#b8d4f0' },
  { offset: 1, color: '#7baed8' },
];

/** 프레임으로 사용 가능한 도형 목록 */
export const FRAME_SHAPES = [
  'rect', 'roundedRect', 'circle', 'triangle', 'star',
  'diamond', 'pentagon', 'hexagon', 'arrow', 'cross', 'heart',
];

// ─── 내부 헬퍼 ───────────────────────────────────────────────

/** 원본 이미지 상태 저장 (프레임 삽입 전 → 추출 시 복원용) */
const saveOriginalProps = (image) => {
  image._origLeft = image.left;
  image._origTop = image.top;
  image._origScaleX = image.scaleX;
  image._origScaleY = image.scaleY;
  image._origWidth = image.width;
  image._origHeight = image.height;
  image._origAngle = image.angle || 0;
  image._origCropX = image.cropX || 0;
  image._origCropY = image.cropY || 0;
};

/** _orig* → 일반 객체로 추출 (스택 저장용) */
const extractOrigProps = (image) => {
  const entry = {};
  ORIG_KEYS.forEach(k => { entry[k] = image[k]; });
  return entry;
};

/** 일반 객체 → 이미지에 _orig* 복원 */
const restoreOrigProps = (image, entry) => {
  ORIG_KEYS.forEach(k => { image[k] = entry[k]; });
};

/** 프레임 메타데이터 모두 제거 */
const clearFrameMetadata = (img) => {
  FRAME_META_KEYS.forEach(k => { img[k] = undefined; });
};

/**
 * 이미지 → 프레임 크기에 맞게 crop/scale/clipPath 설정
 * @returns {number} coverScale
 */
const fitImageToFrame = (image, { frameW, frameH, shapeType, centerX, centerY, angle }) => {
  const el = image.getElement();
  const imgW = el?.naturalWidth || image.width || 100;
  const imgH = el?.naturalHeight || image.height || 100;
  const coverScale = Math.max(frameW / imgW, frameH / imgH);

  const cropW = Math.max(1, Math.round(frameW / coverScale));
  const cropH = Math.max(1, Math.round(frameH / coverScale));
  const cropX = Math.round((imgW - cropW) / 2);
  const cropY = Math.round((imgH - cropH) / 2);

  image.set({
    left: centerX, top: centerY,
    originX: 'center', originY: 'center',
    scaleX: coverScale, scaleY: coverScale,
    angle, cropX, cropY, width: cropW, height: cropH,
  });

  image.clipPath = shapeType !== 'rect'
    ? createFrameClipPath(shapeType, cropW, cropH)
    : null;

  return coverScale;
};

/** 이미지에 프레임 메타데이터 일괄 설정 */
const applyFrameMetadata = (image, { shapeType, frameLocked, coverScale, frameW, frameH, imageStack }) => {
  image._isFrame = true;
  image._isEmptyFrame = false;
  image._frameShapeType = shapeType;
  image._frameLocked = frameLocked;
  image._frameImageScale = coverScale;
  image._frameWidth = frameW;
  image._frameHeight = frameH;
  image._frameImageStack = imageStack || [];
  image.id = `frame-${Date.now()}`;
  image.name = 'Frame';
};

/** 캔버스의 특정 z-index에 객체 삽입 (범위 초과 시 add) */
const insertAtIndex = (canvas, index, obj) => {
  if (index >= 0 && index < canvas.getObjects().length) {
    canvas.insertAt(index, obj);
  } else {
    canvas.add(obj);
  }
};

/** 이미지 원본 크기 (HTMLImageElement 기반) */
const getOriginalSize = (image) => {
  const el = image.getElement();
  return {
    w: el?.naturalWidth || el?.width || image.width,
    h: el?.naturalHeight || el?.height || image.height,
  };
};

/**
 * 프레임 내 crop 좌표 계산 (편집 모드 업데이트 & 종료에서 공유)
 * @param {Object} p - { imgCenterX, imgCenterY, sx, sy, origW, origH, frameCx, frameCy, frameW, frameH }
 * @returns {{ cropW, cropH, cropX, cropY }}
 */
const calcFrameCrop = ({ imgCenterX, imgCenterY, sx, sy, origW, origH, frameCx, frameCy, frameW, frameH }) => {
  const cropW = Math.max(1, Math.round(frameW / sx));
  const cropH = Math.max(1, Math.round(frameH / sy));
  const srcFrameCx = origW / 2 + (frameCx - imgCenterX) / sx;
  const srcFrameCy = origH / 2 + (frameCy - imgCenterY) / sy;
  const cropX = Math.max(0, Math.min(origW - cropW, Math.round(srcFrameCx - cropW / 2)));
  const cropY = Math.max(0, Math.min(origH - cropH, Math.round(srcFrameCy - cropH / 2)));
  return { cropW, cropH, cropX, cropY };
};

// ─── 공개 API ────────────────────────────────────────────────

/**
 * 빈 프레임 도형 생성
 */
export const createEmptyFrame = (shapeType, cx, cy, size = 300) => {
  const common = {
    fill: '#f3f4f6', // 임시 fill (아래서 gradient로 교체)
    ...EMPTY_FRAME_STYLE,
  };

  const obj = createShapeObject(shapeType, common, {
    cx, cy, size,
    fabricFill: '#f3f4f6',
    fabricStroke: EMPTY_FRAME_STYLE.stroke,
    rx: 0, ry: 0,
  });
  if (!obj) return null;

  // 그라데이션 파란색 배경 적용
  const objH = obj.height || size;
  obj.set('fill', new fabric.Gradient({
    type: 'linear',
    coords: { x1: 0, y1: 0, x2: 0, y2: objH },
    colorStops: EMPTY_FRAME_GRADIENT,
  }));

  obj._isFrame = true;
  obj._isEmptyFrame = true;
  obj._frameShapeType = shapeType;
  obj._frameLocked = false;
  obj.id = `frame-${Date.now()}`;
  obj.name = 'Frame';

  return obj;
};

/**
 * 도형 타입에 맞는 clipPath 생성 (이미지 로컬 좌표 기준)
 */
export const createFrameClipPath = (shapeType, width, height, offsetX = 0, offsetY = 0) => {
  const cx = offsetX;
  const cy = offsetY;
  const common = { left: cx, top: cy, originX: 'center', originY: 'center' };

  /** 정다각형 생성 헬퍼 */
  const regularPolygon = (sides, startAngle = -Math.PI / 2) => {
    const r = Math.min(width, height) / 2;
    const pts = [];
    for (let i = 0; i < sides; i++) {
      const angle = (i * 2 * Math.PI) / sides + startAngle;
      pts.push({ x: r + r * Math.cos(angle), y: r + r * Math.sin(angle) });
    }
    return new fabric.Polygon(pts, {
      ...common,
      scaleX: width / (r * 2),
      scaleY: height / (r * 2),
    });
  };

  switch (shapeType) {
    case 'circle':
      return new fabric.Ellipse({ ...common, rx: width / 2, ry: height / 2 });

    case 'roundedRect':
      return new fabric.Rect({ ...common, width, height, rx: 20, ry: 20 });

    case 'triangle':
      return new fabric.Triangle({ ...common, width, height });

    case 'star': {
      const outerR = Math.min(width, height) / 2;
      const innerR = outerR * 0.4;
      const pts = [];
      const step = Math.PI / 5;
      for (let i = 0; i < 10; i++) {
        const r = i % 2 === 0 ? outerR : innerR;
        const angle = i * step - Math.PI / 2;
        pts.push({ x: outerR + r * Math.cos(angle), y: outerR + r * Math.sin(angle) });
      }
      return new fabric.Polygon(pts, {
        ...common,
        scaleX: width / (outerR * 2),
        scaleY: height / (outerR * 2),
      });
    }

    case 'diamond': {
      const s = Math.min(width, height);
      const pts = [
        { x: s / 2, y: 0 }, { x: s, y: s / 2 },
        { x: s / 2, y: s }, { x: 0, y: s / 2 },
      ];
      return new fabric.Polygon(pts, { ...common, scaleX: width / s, scaleY: height / s });
    }

    case 'pentagon':
      return regularPolygon(5);

    case 'hexagon':
      return regularPolygon(6);

    case 'arrow': {
      const w = width, h = height;
      const shaftW = h * 0.35;
      const headStart = w * 0.55;
      const ccy = h / 2;
      const pts = [
        { x: 0, y: ccy - shaftW / 2 },
        { x: headStart, y: ccy - shaftW / 2 },
        { x: headStart, y: 0 },
        { x: w, y: ccy },
        { x: headStart, y: h },
        { x: headStart, y: ccy + shaftW / 2 },
        { x: 0, y: ccy + shaftW / 2 },
      ];
      return new fabric.Polygon(pts, common);
    }

    case 'cross': {
      const s = Math.min(width, height);
      const t = s / 3;
      const pts = [
        { x: t, y: 0 }, { x: 2 * t, y: 0 },
        { x: 2 * t, y: t }, { x: s, y: t },
        { x: s, y: 2 * t }, { x: 2 * t, y: 2 * t },
        { x: 2 * t, y: s }, { x: t, y: s },
        { x: t, y: 2 * t }, { x: 0, y: 2 * t },
        { x: 0, y: t }, { x: t, y: t },
      ];
      return new fabric.Polygon(pts, { ...common, scaleX: width / s, scaleY: height / s });
    }

    case 'heart': {
      const s = Math.min(width, height);
      const sc = s / 100;
      const d = `M ${50 * sc} ${90 * sc} C ${50 * sc} ${90 * sc} ${10 * sc} ${60 * sc} ${10 * sc} ${35 * sc} C ${10 * sc} ${15 * sc} ${30 * sc} ${5 * sc} ${50 * sc} ${25 * sc} C ${70 * sc} ${5 * sc} ${90 * sc} ${15 * sc} ${90 * sc} ${35 * sc} C ${90 * sc} ${60 * sc} ${50 * sc} ${90 * sc} ${50 * sc} ${90 * sc} Z`;
      return new fabric.Path(d, { ...common, scaleX: width / s, scaleY: height / s });
    }

    case 'rect':
    default:
      return new fabric.Rect({ ...common, width, height });
  }
};

// ─── 프레임 삽입 / 교체 / 추출 ──────────────────────────────

/**
 * 빈 프레임에 이미지 삽입
 */
export const insertImageIntoFrame = (frame, image, canvas) => {
  const frameCenter = frame.getCenterPoint();
  const frameW = (frame.width || 100) * (frame.scaleX || 1);
  const frameH = (frame.height || 100) * (frame.scaleY || 1);
  const shapeType = frame._frameShapeType || 'rect';
  const frameLocked = frame._frameLocked || false;
  const frameAngle = frame.angle || 0;

  saveOriginalProps(image);

  const frameIndex = canvas.getObjects().indexOf(frame);
  canvas.remove(frame);
  canvas.remove(image);

  const coverScale = fitImageToFrame(image, {
    frameW, frameH, shapeType,
    centerX: frameCenter.x, centerY: frameCenter.y, angle: frameAngle,
  });

  applyFrameMetadata(image, {
    shapeType, frameLocked, coverScale, frameW, frameH, imageStack: [],
  });

  insertAtIndex(canvas, frameIndex, image);
  canvas.setActiveObject(image);
  canvas.renderAll();
  return image;
};

/**
 * 프레임 내 이미지 교체 (기존 이미지는 스택에 저장)
 */
export const replaceImageInFrame = (frameImg, newImage, canvas) => {
  const frameW = frameImg._frameWidth || (frameImg.width * frameImg.scaleX);
  const frameH = frameImg._frameHeight || (frameImg.height * frameImg.scaleY);
  const shapeType = frameImg._frameShapeType || 'rect';
  const frameLocked = frameImg._frameLocked || false;

  const prevStack = frameImg._frameImageStack || [];
  const stackEntry = { element: frameImg.getElement(), ...extractOrigProps(frameImg) };

  saveOriginalProps(newImage);

  const frameIndex = canvas.getObjects().indexOf(frameImg);
  const center = frameImg.getCenterPoint();
  const angle = frameImg.angle || 0;

  canvas.remove(frameImg);
  canvas.remove(newImage);

  const coverScale = fitImageToFrame(newImage, {
    frameW, frameH, shapeType,
    centerX: center.x, centerY: center.y, angle,
  });

  applyFrameMetadata(newImage, {
    shapeType, frameLocked, coverScale, frameW, frameH,
    imageStack: [...prevStack, stackEntry],
  });

  insertAtIndex(canvas, frameIndex, newImage);
  canvas.setActiveObject(newImage);
  canvas.renderAll();
  return newImage;
};

/**
 * 프레임에서 이미지 빼내기
 * 스택에 이전 이미지가 있으면 프레임에 복원, 없으면 빈 프레임 생성
 */
export const extractImageFromFrame = (frameImg, canvas) => {
  const center = frameImg.getCenterPoint();
  const frameW = frameImg._frameWidth || (frameImg.width * frameImg.scaleX);
  const frameH = frameImg._frameHeight || (frameImg.height * frameImg.scaleY);
  const shapeType = frameImg._frameShapeType || 'rect';
  const frameLocked = frameImg._frameLocked || false;
  const angle = frameImg.angle || 0;
  const frameIndex = canvas.getObjects().indexOf(frameImg);
  const imageStack = frameImg._frameImageStack || [];

  // ── 1. 현재 이미지를 독립 이미지로 복원 ──
  frameImg.clipPath = null;

  if (frameImg._origLeft !== undefined) {
    // 프론트에서 삽입된 이미지: 원본 상태로 복원
    frameImg.set({
      left: frameImg._origLeft, top: frameImg._origTop,
      scaleX: frameImg._origScaleX, scaleY: frameImg._origScaleY,
      width: frameImg._origWidth, height: frameImg._origHeight,
      angle: frameImg._origAngle,
      cropX: frameImg._origCropX, cropY: frameImg._origCropY,
    });
  } else {
    // 파서 로드 프레임: coverScale 기반으로 원본 크기 복원
    const { w: origW, h: origH } = getOriginalSize(frameImg);
    const origScale = frameImg._frameImageScale || frameImg.scaleX;
    frameImg.set({
      cropX: 0, cropY: 0, width: origW, height: origH,
      scaleX: origScale, scaleY: origScale,
      left: center.x + frameW / 2 + origW * origScale / 2 + 20,
      top: center.y,
    });
  }

  clearFrameMetadata(frameImg);
  frameImg.id = `image-${Date.now()}`;
  frameImg.name = 'Image';
  frameImg.setCoords();
  applySelectionStyle(frameImg);

  // ── 2. 프레임 복원 (스택 또는 빈 프레임) ──
  if (imageStack.length > 0) {
    const prev = imageStack.pop();
    const restoredImg = new fabric.FabricImage(prev.element, {
      originX: 'center', originY: 'center',
    });

    const coverScale = fitImageToFrame(restoredImg, {
      frameW, frameH, shapeType,
      centerX: center.x, centerY: center.y, angle,
    });

    applyFrameMetadata(restoredImg, {
      shapeType, frameLocked, coverScale, frameW, frameH, imageStack,
    });
    restoreOrigProps(restoredImg, prev);
    applySelectionStyle(restoredImg);

    insertAtIndex(canvas, frameIndex, restoredImg);
  } else {
    const emptyFrame = createEmptyFrame(
      shapeType,
      center.x - frameW / 2,
      center.y - frameH / 2,
      Math.min(frameW, frameH),
    );
    if (emptyFrame) {
      emptyFrame.set({
        left: center.x, top: center.y,
        originX: 'center', originY: 'center',
        scaleX: frameW / (emptyFrame.width || 1),
        scaleY: frameH / (emptyFrame.height || 1),
        angle,
      });
      emptyFrame._frameLocked = frameLocked;
      insertAtIndex(canvas, frameIndex, emptyFrame);
    }
  }

  canvas.setActiveObject(frameImg);
  canvas.renderAll();
};

// ─── 히트 테스트 ─────────────────────────────────────────────

/**
 * 이미지가 프레임과 충분히 겹치는지 판정 (프레임 짧은 변의 30% 이상)
 */
export const isImageNearFrame = (frame, image, overlapRatio = 0.3) => {
  if (!frame || !image) return false;
  if (frame._frameLocked) return false;

  const fBound = frame.getBoundingRect();
  const iBound = image.getBoundingRect();

  const minOverlap = Math.min(fBound.width, fBound.height) * overlapRatio;

  const overlapX = Math.min(iBound.left + iBound.width, fBound.left + fBound.width)
                 - Math.max(iBound.left, fBound.left);
  const overlapY = Math.min(iBound.top + iBound.height, fBound.top + fBound.height)
                 - Math.max(iBound.top, fBound.top);

  return overlapX >= minOverlap && overlapY >= minOverlap;
};

/**
 * 캔버스에서 드래그 중인 이미지에 가장 가까운 프레임 찾기
 */
export const findNearestFrame = (canvas, movingImage) => {
  if (!canvas || !movingImage) return null;

  let bestFrame = null;
  let bestDist = Infinity;
  const imgCenter = movingImage.getCenterPoint();

  for (const obj of canvas.getObjects()) {
    if (obj === movingImage || !obj._isFrame || obj._frameLocked) continue;
    if (!isImageNearFrame(obj, movingImage)) continue;

    const fc = obj.getCenterPoint();
    const dist = (imgCenter.x - fc.x) ** 2 + (imgCenter.y - fc.y) ** 2;

    if (dist < bestDist) {
      bestDist = dist;
      bestFrame = obj;
    }
  }

  return bestFrame;
};

// ─── 프레임 편집 모드 ────────────────────────────────────────

/**
 * 프레임 편집 모드 진입 (더블클릭)
 * 어두운 오버레이 + 반투명 전체 이미지 + 밝은 프레임 영역 + 점선 외곽선
 */
export const enterFrameEditMode = (frameImg, canvas) => {
  const savedOpacity = frameImg.opacity;
  const frameW = frameImg._frameWidth || (frameImg.width * frameImg.scaleX);
  const frameH = frameImg._frameHeight || (frameImg.height * frameImg.scaleY);
  const shapeType = frameImg._frameShapeType || 'rect';
  const frameCenter = frameImg.getCenterPoint();
  const frameAngle = frameImg.angle || 0;

  // crop 상태 저장
  const savedCropX = frameImg.cropX || 0;
  const savedCropY = frameImg.cropY || 0;
  const savedWidth = frameImg.width;
  const savedHeight = frameImg.height;
  const { w: origW, h: origH } = getOriginalSize(frameImg);
  const hasCrop = (savedCropX > 0 || savedCropY > 0 ||
                   savedWidth < origW - 1 || savedHeight < origH - 1);

  // ── 다른 객체 이벤트 비활성화 ──
  const savedEventedStates = [];
  canvas.getObjects().forEach(obj => {
    if (obj === frameImg) return;
    savedEventedStates.push({ obj, evented: obj.evented, selectable: obj.selectable });
    obj.set({ evented: false, selectable: false });
  });

  // ── 어두운 오버레이 (대형 고정 크기 — 모든 뷰포트 모드에서 동작) ──
  const darkOverlay = new fabric.Rect({
    left: -10000, top: -10000,
    width: 30000, height: 30000,
    fill: 'rgba(0,0,0,0.7)',
    originX: 'left', originY: 'top',
    selectable: false, evented: false,
  });
  darkOverlay._isFrameEditOverlay = true;
  canvas.add(darkOverlay);

  // ── crop 해제 → 전체 이미지 표시 (반투명) ──
  frameImg.clipPath = null;
  frameImg.set({ opacity: 0.5 });

  if (hasCrop) {
    const sx = frameImg.scaleX;
    const sy = frameImg.scaleY;
    frameImg.set({
      cropX: 0, cropY: 0,
      width: origW, height: origH,
      left: frameCenter.x + (origW / 2 - (savedCropX + savedWidth / 2)) * sx,
      top: frameCenter.y + (origH / 2 - (savedCropY + savedHeight / 2)) * sy,
    });
  }

  frameImg.lockScalingFlip = true;
  frameImg.setCoords();
  canvas.bringObjectToFront(frameImg);
  frameImg.setCoords();

  // ── 밝은 프레임 영역 (고정 위치, cropX/cropY로 내용 표시) ──
  const sx = frameImg.scaleX;
  const sy = frameImg.scaleY;
  const { cropW, cropH } = calcFrameCrop({
    imgCenterX: frameCenter.x, imgCenterY: frameCenter.y,
    sx, sy, origW, origH,
    frameCx: frameCenter.x, frameCy: frameCenter.y, frameW, frameH,
  });

  const brightClip = new fabric.FabricImage(frameImg.getElement(), {
    left: frameCenter.x, top: frameCenter.y,
    originX: 'center', originY: 'center',
    scaleX: sx, scaleY: sy,
    width: cropW, height: cropH,
    cropX: savedCropX, cropY: savedCropY,
    angle: frameAngle,
    opacity: 1,
    selectable: false, evented: false,
  });
  if (shapeType !== 'rect') {
    brightClip.clipPath = createFrameClipPath(shapeType, cropW, cropH);
  }
  brightClip._isFrameEditBright = true;
  canvas.add(brightClip);

  // ── 프레임 테두리 외곽선 ──
  const outline = createFrameClipPath(shapeType, frameW, frameH);
  outline.set({
    left: frameCenter.x, top: frameCenter.y,
    originX: 'center', originY: 'center',
    fill: 'transparent',
    stroke: '#06b6d4', strokeWidth: 2,
    strokeDashArray: [6, 3],
    selectable: false, evented: false,
    angle: frameAngle,
  });
  outline._isFrameEditOutline = true;
  canvas.add(outline);

  canvas.setActiveObject(frameImg);

  return {
    frameImg, savedOpacity, darkOverlay, brightClip, outline,
    frameW, frameH, shapeType, hasCrop, origW, origH,
    frameCenter: { x: frameCenter.x, y: frameCenter.y },
    frameAngle, savedEventedStates,
    // 스케일/위치 제한용 이전 상태 (초기값 = 편집 진입 시)
    _prevSx: frameImg.scaleX,
    _prevSy: frameImg.scaleY,
    _prevLeft: frameImg.left,
    _prevTop: frameImg.top,
  };
};

/**
 * 프레임 편집 모드 중 이미지 이동/스케일 시 호출
 * - 스케일 제한: 이미지 4변이 프레임 4변을 항상 커버
 * - 이동 제한: 이미지가 프레임 밖으로 벗어나지 않도록
 * - 밝은 영역 동기화
 */
export const updateFrameEditPosition = (editState, canvas) => {
  if (!editState) return;
  const { frameImg, brightClip, frameCenter, frameW, frameH, origW, origH, shapeType } = editState;

  const fLeft = frameCenter.x - frameW / 2;
  const fRight = frameCenter.x + frameW / 2;
  const fTop = frameCenter.y - frameH / 2;
  const fBottom = frameCenter.y + frameH / 2;

  // ── 스케일/위치 제한: 프레임 커버 위반 시 이전 상태로 복원 ──
  const newCenter = frameImg.getCenterPoint();
  const newHalfW = origW / 2 * frameImg.scaleX;
  const newHalfH = origH / 2 * frameImg.scaleY;
  const eps = 0.5;

  if (newCenter.x - newHalfW > fLeft + eps || newCenter.x + newHalfW < fRight - eps) {
    frameImg.scaleX = editState._prevSx;
    frameImg.left = editState._prevLeft;
  }
  if (newCenter.y - newHalfH > fTop + eps || newCenter.y + newHalfH < fBottom - eps) {
    frameImg.scaleY = editState._prevSy;
    frameImg.top = editState._prevTop;
  }

  const sx = frameImg.scaleX;
  const sy = frameImg.scaleY;

  // ── 이동 제한 ──
  const imgHalfW = origW / 2 * sx;
  const imgHalfH = origH / 2 * sy;
  let cx = frameImg.getCenterPoint().x, cy = frameImg.getCenterPoint().y;
  if (cx - imgHalfW > fLeft) cx = fLeft + imgHalfW;
  if (cx + imgHalfW < fRight) cx = fRight - imgHalfW;
  if (cy - imgHalfH > fTop) cy = fTop + imgHalfH;
  if (cy + imgHalfH < fBottom) cy = fBottom - imgHalfH;
  frameImg.set({ left: cx, top: cy });

  // 다음 프레임 비교용 상태 저장
  editState._prevSx = frameImg.scaleX;
  editState._prevSy = frameImg.scaleY;
  editState._prevLeft = frameImg.left;
  editState._prevTop = frameImg.top;

  // ── 밝은 영역 동기화 ──
  const crop = calcFrameCrop({
    imgCenterX: cx, imgCenterY: cy, sx, sy, origW, origH,
    frameCx: frameCenter.x, frameCy: frameCenter.y, frameW, frameH,
  });

  brightClip.set({
    left: frameCenter.x, top: frameCenter.y,
    scaleX: sx, scaleY: sy,
    width: crop.cropW, height: crop.cropH,
    cropX: crop.cropX, cropY: crop.cropY,
  });
  if (shapeType !== 'rect') {
    brightClip.clipPath = createFrameClipPath(shapeType, crop.cropW, crop.cropH);
  }
  brightClip.setCoords();

  canvas.renderAll();
};

/**
 * 프레임 편집 모드 종료 — crop 재계산 후 프레임 상태 복원
 */
export const exitFrameEditMode = (editState, canvas) => {
  if (!editState) return;

  const { frameImg, savedOpacity, darkOverlay, brightClip, outline,
          frameW, frameH, shapeType, origW, origH,
          frameCenter: origFrameCenter, savedEventedStates } = editState;

  // 오버레이 제거 + 편집 모드 설정 복원
  frameImg.lockScalingFlip = false;
  [darkOverlay, brightClip, outline].forEach(o => { if (o) canvas.remove(o); });

  // 다른 객체 이벤트 상태 복원
  if (savedEventedStates) {
    savedEventedStates.forEach(({ obj, evented, selectable }) => {
      if (canvas.getObjects().includes(obj)) {
        obj.set({ evented, selectable });
      }
    });
  }

  // crop 재계산
  const imgCenter = frameImg.getCenterPoint();
  const sx = frameImg.scaleX;
  const sy = frameImg.scaleY;
  const crop = calcFrameCrop({
    imgCenterX: imgCenter.x, imgCenterY: imgCenter.y, sx, sy, origW, origH,
    frameCx: origFrameCenter.x, frameCy: origFrameCenter.y, frameW, frameH,
  });

  frameImg.set({
    cropX: crop.cropX, cropY: crop.cropY,
    width: crop.cropW, height: crop.cropH,
    left: origFrameCenter.x, top: origFrameCenter.y,
    opacity: savedOpacity,
  });

  frameImg.clipPath = shapeType !== 'rect'
    ? createFrameClipPath(shapeType, crop.cropW, crop.cropH)
    : null;

  frameImg._frameWidth = frameW;
  frameImg._frameHeight = frameH;
  frameImg._frameImageScale = frameImg.scaleX;

  canvas.renderAll();
};
